import { useMemo, useRef, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { Avatar } from '../components/Avatar'
import { useApp } from '../store/AppContext'
import { DAY_LABELS, WEEK_DAYS, dayOfWeekFor, shiftDateLabel } from '../lib/cover'
import { localISODate } from '../lib/date'
import { profilePhotoFor, removeProfilePhoto, resizeProfilePhoto, saveProfilePhoto } from '../lib/profilePhotos'
import type { DayOfWeek } from '../types'

const shortLabel: Record<DayOfWeek, string> = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' }

function nextSevenDays() {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i); return localISODate(d)
  })
}

function CameraIcon() {
  return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M4 8.5h3l1.2-2h7.6l1.2 2h3v10H4z"/><circle cx="12" cy="13.5" r="3.2"/></svg>
}

function CalendarIcon() {
  return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M5 4v3M19 4v3M4 9h16M5 6h14a1 1 0 0 1 1 1v12H4V7a1 1 0 0 1 1-1Z"/></svg>
}

export function CaseyLeoProfile() {
  const { currentUser, getAvailability, updateAvailability } = useApp()
  const saved = getAvailability(currentUser.id)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [days, setDays] = useState<DayOfWeek[]>(saved.availableDays)
  const [showOnCalendar, setShowOnCalendar] = useState(saved.showOnCalendar)
  const [justSaved, setJustSaved] = useState(false)
  const [photoVersion, setPhotoVersion] = useState(0)
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const upcoming = useMemo(nextSevenDays, [])
  const chosen = WEEK_DAYS.filter(d => days.includes(d))
  const hasPhoto = useMemo(() => Boolean(profilePhotoFor(currentUser.id)), [currentUser.id, photoVersion])

  const toggleDay = (day: DayOfWeek) => setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  const save = () => {
    updateAvailability({ userId: currentUser.id, availableDays: chosen, showOnCalendar })
    setJustSaved(true)
    setTimeout(() => setJustSaved(false), 1500)
  }

  const choosePhoto = async (file?: File) => {
    if (!file) return
    setPhotoBusy(true)
    setPhotoError('')
    try {
      const dataUrl = await resizeProfilePhoto(file)
      saveProfilePhoto(currentUser.id, dataUrl)
      setPhotoVersion(v => v + 1)
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : 'Could not upload that photo.')
    } finally {
      setPhotoBusy(false)
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const removePhoto = () => {
    removeProfilePhoto(currentUser.id)
    setPhotoVersion(v => v + 1)
    setPhotoError('')
  }

  return (
    <div className="min-h-screen bg-white pb-28 text-slate-900">
      <TopBar title="My Profile" subtitle="Your profile photo and availability" />
      <div className="flex flex-col gap-5 px-4 py-4">
        <section className="rounded-[24px] border border-[#cfdde9] bg-white p-4 shadow-[0_8px_24px_rgba(55,83,110,.055)]">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="rounded-full border-[3px] border-[#6f93b8] p-[2px]"><Avatar key={photoVersion} user={currentUser} size={68} /></div>
              <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoBusy} aria-label="Change profile photo" className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#6f93b8] text-white shadow disabled:opacity-60"><CameraIcon /></button>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={e => void choosePhoto(e.target.files?.[0])} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-[.12em] text-[#7897b5]">My profile</p>
              <h2 className="mt-1 truncate text-[19px] font-black text-[#2d3d4d]">{currentUser.name}</h2>
              <p className="mt-0.5 text-[13px] font-medium text-[#7c8996]">Independent Support Worker · Leo’s team</p>
              <div className="mt-2 flex flex-wrap gap-3">
                <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoBusy} className="text-xs font-black text-[#6f93b8]">{photoBusy ? 'Uploading…' : hasPhoto ? 'Change photo' : 'Add photo'}</button>
                {hasPhoto && <button type="button" onClick={removePhoto} className="text-xs font-black text-slate-400">Remove photo</button>}
              </div>
              {photoError && <p className="mt-2 text-xs font-semibold text-red-600">{photoError}</p>}
            </div>
          </div>
        </section>

        <section className="rounded-[20px] border border-[#d8e2ea] bg-white p-4 shadow-[0_5px_18px_rgba(55,83,110,.045)]">
          <div className="flex items-center gap-2 text-[#6f93b8]"><CalendarIcon/><p className="text-[11px] font-black uppercase tracking-[.1em]">Leo’s support team</p></div>
          <div className="mt-3 grid gap-2 text-[13px] leading-5 text-[#61707e]">
            <p><span className="font-bold text-[#425361]">Participant:</span> Leo</p>
            <p><span className="font-bold text-[#425361]">Primary support:</span> After-school routines, community access, daily living and therapy strategy implementation.</p>
            <p><span className="font-bold text-[#425361]">Access:</span> Leo’s shared calendar, shifts, tasks, routines, approved documents, team updates, messages, handover and onboarding.</p>
          </div>
        </section>

        <section>
          <h3 className="text-[15px] font-black text-[#334453]">My availability</h3>
          <p className="mt-1 text-[12px] leading-5 text-[#7c8996]">Choose the days you are generally available for Leo. Sharing availability never books or assigns you to a shift.</p>
          <div className="mt-3 flex flex-wrap gap-2">{WEEK_DAYS.map(day => <button key={day} onClick={() => toggleDay(day)} aria-pressed={days.includes(day)} className={`rounded-xl border px-3.5 py-2.5 text-xs font-semibold ${days.includes(day) ? 'border-[#8fc1b6] bg-[#eef7f4] text-[#5d887f]' : 'border-[#d8e2ea] bg-white text-[#7c8996]'}`}>{shortLabel[day]}</button>)}</div>

          <div className="mt-4 rounded-[20px] border border-[#d8e2ea] bg-white p-4 shadow-[0_5px_18px_rgba(55,83,110,.045)]">
            <p className="text-[11px] font-black uppercase tracking-[.08em] text-[#7897b5]">Currently available</p>
            <p className="mt-1 text-[14px] font-bold text-[#425361]">{chosen.length === 0 ? 'No days selected' : chosen.length === 7 ? 'Every day' : chosen.map(d => DAY_LABELS[d]).join(', ')}</p>
            <p className="mt-4 text-[11px] font-black uppercase tracking-[.08em] text-[#7897b5]">Next 7 days</p>
            <div className="mt-2 flex flex-wrap gap-2">{upcoming.map(date => { const free = showOnCalendar && days.includes(dayOfWeekFor(date)); return <span key={date} className={`flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold ${free ? 'bg-[#eef7f4] text-[#5d887f]' : 'bg-[#f7f9fb] text-[#9aa4ae]'}`}><span className={`h-2 w-2 rounded-full ${free ? 'bg-[#7cab9f]' : 'bg-[#cbd4dc]'}`} />{shiftDateLabel(date)}</span> })}</div>
          </div>

          <div className="mt-4 flex items-center justify-between rounded-[20px] border border-[#d8e2ea] bg-white p-4 shadow-[0_5px_18px_rgba(55,83,110,.045)]">
            <div className="pr-3"><p className="text-[13px] font-bold text-[#425361]">Show availability on Leo’s team calendar</p><p className="mt-1 text-[12px] leading-5 text-[#8a96a1]">Shared days appear as an availability indicator for the people coordinating Leo’s support.</p></div>
            <button onClick={() => setShowOnCalendar(v => !v)} aria-pressed={showOnCalendar} className={`relative h-7 w-12 shrink-0 rounded-full transition ${showOnCalendar ? 'bg-[#7cab9f]' : 'bg-[#dce3e9]'}`}><span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${showOnCalendar ? 'left-[22px]' : 'left-0.5'}`} /></button>
          </div>
          <button onClick={save} className="mt-4 w-full rounded-2xl bg-[#6f93b8] py-3 text-sm font-black text-white shadow-[0_5px_14px_rgba(78,111,145,.16)]">{justSaved ? 'Saved' : 'Save availability'}</button>
        </section>
      </div>
    </div>
  )
}

